import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { formatDate } from '../client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['new', 'in_progress', 'scheduled', 'completed', 'declined'];
const STATUS_BADGES = {
  new: 'bg-amber-50 text-amber-800 ring-amber-200',
  in_progress: 'bg-sky-50 text-sky-700 ring-sky-200',
  scheduled: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  declined: 'bg-rose-50 text-rose-700 ring-rose-200',
};

export default function AdminPostSettlementPanel({ clientId, token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/clients/${clientId}/post-settlement`, { headers: auth });
    if (r.ok) setItems(await r.json());
  };
  useEffect(() => { (async () => { try { await reload(); } finally { setLoading(false); } })(); /* eslint-disable-next-line */ }, [clientId]);

  const update = async (id, body) => {
    await fetch(`${backendUrl}/api/admin/post-settlement/${id}`, {
      method: 'PATCH',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    reload();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-5 space-y-2.5">
      <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{items.length} request(s)</span>
      {items.length === 0 && <div className="text-slate-500 text-sm py-4">No post-settlement requests yet.</div>}
      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r.id} className="rounded-xl ring-1 ring-slate-200 bg-white p-3 text-sm">
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{r.option_title}</div>
                {r.notes && <div className="text-slate-500 text-xs mt-0.5">{r.notes}</div>}
                {r.preferred_time && <div className="text-xs text-slate-500 mt-0.5">Preferred: {r.preferred_time}</div>}
                <div className="text-[11px] text-slate-400 mt-1">Submitted {formatDate(r.created_at)}</div>
              </div>
              <select
                value={r.status}
                onChange={(e) => update(r.id, { status: e.target.value })}
                className={`px-2 py-1 text-xs rounded ring-1 ${STATUS_BADGES[r.status] || ''}`}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <ReplyBox onSend={(reply) => update(r.id, { broker_reply: reply })} existing={r.broker_reply} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReplyBox({ onSend, existing }) {
  const [val, setVal] = useState('');
  return (
    <div className="mt-2">
      {existing && (
        <div className="text-xs text-slate-700 bg-slate-50 ring-1 ring-slate-200 rounded p-2 mb-1.5 whitespace-pre-wrap">
          <span className="text-emerald-700 font-medium">Reply sent:</span> {existing}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Send a reply to the client…"
          className="flex-1 px-2.5 py-1.5 rounded ring-1 ring-slate-200 text-xs"
        />
        <button
          onClick={() => { if (val.trim()) { onSend(val); setVal(''); } }}
          disabled={!val.trim()}
          className="inline-flex items-center gap-1 px-3 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs disabled:opacity-50"
        >
          <Send className="w-3 h-3" /> Reply
        </button>
      </div>
    </div>
  );
}
